import type { Handler, NextFn, RequestContext } from '@ember-data/request';
import type Store from '@ember-data/store';

// The slice of the application adapter this handler borrows. Builder requests
// (`store.request(...)`) skip the adapter, so headers, the 401/403 refresh and
// the logout decision are taken from it here, keeping one copy of that logic
// while models move off adapters one at a time.
interface AuthAdapter {
  headers: Record<string, string | undefined>;
  session: { refreshToken(): Promise<void> };
  handleRefreshFailure(refreshError: unknown, originalError: unknown): never;
}

const isAuthError = (error: unknown) => {
  const status = (error as { status?: number } | null)?.status;
  return status === 401 || status === 403;
};

export default class AuthHandler implements Handler {
  constructor(private store: Store) {}

  // The store implements adapterFor, but useLegacyStore's types leave it out.
  get adapter() {
    const store = this.store as unknown as {
      adapterFor(modelName: string): AuthAdapter;
    };
    return store.adapterFor('application');
  }

  send<T>(context: RequestContext, next: NextFn<T>) {
    const headers = new Headers(context.request.headers);
    for (const [key, value] of Object.entries(this.adapter.headers)) {
      if (value) headers.set(key, value);
    }
    return next({ ...context.request, headers });
  }

  async request<T>(context: RequestContext, next: NextFn<T>) {
    try {
      return await this.send(context, next);
    } catch (error) {
      if (!isAuthError(error)) throw error;
      try {
        await this.adapter.session.refreshToken();
      } catch (refreshError) {
        this.adapter.handleRefreshFailure(refreshError, error);
      }
      // A second 401/403 on a fresh token is a real refusal: let it surface.
      return await this.send(context, next);
    }
  }
}
